import React from 'react'
import { Space } from 'antd'
import Editor from '../../../../components/common/Editor/Editor'
import FileCriteriaReasoning from './file-criteria-reasoning/FileCriteriaReasoning'
import SubmissionUnitTestResults from './submission-unit-test-results/SubmissionUnitTestResults'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import { useDispatch, useSelector } from 'react-redux'
import SubmissionVersionSelector from './submission-version-selector/SubmissionVersionSelector'
import Text from 'antd/es/typography/Text'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import { isMba } from '../../../../utils/assignment.helper'
import { getColorByIndex } from '../../../../utils/colors.helper'
import { useParams } from 'react-router'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { useFetchSubmissionCriteriaDetailsQuery } from '../../../../store/apis/semantic-language.api'
import { useFetchSubmissionVersionsQuery } from '../../../../store/apis/submission.api'
import { DEFAULT_SUBMISSION_VERSION } from '../../../../store/slices/submission.slice'
import submissionApiSelectors from '../../../../store/apis/utils/selectors/submission.api.selectors'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import ReviewSubmission from '../review-submission/ReviewSubmission'
import FileGroupYReasoning from './file-group-y-reasoning/FileGroupYReasoning'
import clusterSelectors from '../../../../store/selectors/cluster.selectors'
import { useFetchLayerPatternsQuery } from '../../../../store/apis/pattern.api'

const FileDrawerEditor = () => {
    const language = useSelector(assignmentSelectors.language)
    const type = useSelector(assignmentSelectors.type)
    const isFileInGroupX = useSelector(submissionSelectors.isOpenedSubmissionInGroupX)
    const isFileInGroupY = useSelector(submissionSelectors.isOpenedSubmissionInGroupY)
    const groupX = useSelector(clusterSelectors.groupX)
    const drawer = useSelector(state => state.assignment.drawer)
    const layers = useSelector(state => state.assignment.layers)
    const selectedVersionId = useSelector(submissionSelectors.selectedVersionId)
    const semanticPatternExists = useSelector(patternSelectors.semanticPatternExists)
    const finetunedPatternExists = useSelector(patternSelectors.finetunedPatternExists)

    const dispatch = useDispatch()

    const { token, layerId } = useParams()

    useFetchSubmissionCriteriaDetailsQuery(
        { token, layerId, submissionId: drawer?.file?.id },
        { skip: !semanticPatternExists },
    )
    const { data: submissionVersion } = useFetchSubmissionVersionsQuery(
        { token, layerId, submissionId: drawer?.file?.id },
        {
            skip: selectedVersionId === DEFAULT_SUBMISSION_VERSION,
            selectFromResult: result => submissionApiSelectors.selectVersionById({ id: selectedVersionId }, result),
        },
    )

    const { data: patterns } = useFetchLayerPatternsQuery({ token, layerId })

    const handleGutterClick = (id, startRow, endRow) => {
        dispatch(actions.setEditingPattern({ id, startRow, endRow }))
        dispatch(actions.setShowEditPatternModal(true))
    }

    const currentLayerWeight = layers.find(layer => layer.layer_id === Number(layerId))?.layer_weight

    let formattedPatternList = []
    if (!submissionVersion || drawer.file.content === submissionVersion.content) {
        formattedPatternList = drawer?.file?.motifList?.map(motif => {
            return {
                motifId: motif.motif_id,
                startRow: motif.start_row,
                endRow: motif.end_row,
                color: getColorByIndex(motif.color_id).value,
            }
        })
    } else if (submissionVersion) {
        formattedPatternList = submissionVersion?.patterns?.map(pattern => {
            const patternDetails = patterns?.find(p => p.id === pattern.id)
            return {
                motifId: pattern.id,
                startRow: pattern.startRow,
                endRow: pattern.endRow,
                color: getColorByIndex(patternDetails?.colorId).value,
            }
        })
    }

    const editorHeader = (
        <div className='d-flex-space-between'>
            {isMba(type) && <SubmissionVersionSelector />}
            {isMba(type) && isFileInGroupX && (
                <label>
                    <Text type='secondary' className='mr-8'>
                        Grade
                    </Text>
                    {Math.round(
                        ((submissionVersion?.grade ?? drawer.file.grade ?? groupX.grade) / 100) * currentLayerWeight,
                    )}{' '}
                    / {currentLayerWeight}
                </label>
            )}
        </div>
    )

    return (
        <Space direction='vertical' size='middle' style={{ display: 'flex' }}>
            <Editor
                title={editorHeader}
                value={submissionVersion?.content ?? drawer.file.content}
                language={language}
                readOnly={true}
                patternsList={formattedPatternList}
                onGutterClick={handleGutterClick}
                gutterUpdateTrigger={formattedPatternList}
            />
            {drawer.file.group_y_reason && <FileGroupYReasoning />}
            {!isFileInGroupY && <FileCriteriaReasoning />}
            <SubmissionUnitTestResults />
            {finetunedPatternExists && <ReviewSubmission />}
        </Space>
    )
}

export default FileDrawerEditor
